/**
 * useLiveSessions Hook
 *
 * Real-time listener for the Live tab.
 * Subscribes to the engagement collection (ordered by lastSeen) and builds
 * a running feed of incoming events — only events that arrive AFTER the hook
 * mounts are shown (the initial snapshot establishes a baseline, not a flood).
 *
 * Only call this when the Live tab is active; the subscription is automatically
 * torn down when the component unmounts (tab switch / navigation).
 *
 * @param {boolean} enabled      - Pass false to skip the subscription entirely.
 * @param {string}  environment  - 'production' | 'staging' | 'all'
 * @param {boolean} hideInternal - Exclude the team's own allowlisted-IP traffic
 *   (default true). Safe as a live query filter (unlike the aggregation
 *   functions) since this only ever shows recently-active sessions, which will
 *   have the field set going forward — no large historical backfill concern.
 * @returns {{ feed, activeSessions, connected }}
 */

import { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const LIVE_SESSION_LIMIT = 15;
const FEED_MAX_LENGTH = 50;

export const useLiveSessions = (enabled = true, environment = 'production', hideInternal = true) => {
  // Ordered list of incoming event objects to render as feed cards
  const [feed, setFeed] = useState([]);
  // Most recently active sessions (raw Firestore data, for the session strip)
  const [activeSessions, setActiveSessions] = useState([]);
  // Whether the Firestore connection is established
  const [connected, setConnected] = useState(false);

  // Track previous event counts per session to diff for new events
  const prevCountsRef = useRef(new Map());
  const isInitialRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    // Reset state on (re)mount
    prevCountsRef.current = new Map();
    isInitialRef.current = true;
    setFeed([]);
    setConnected(false);

    const constraints = [
      orderBy('lastSeen', 'desc'),
      limit(LIVE_SESSION_LIMIT),
    ];
    if (environment !== 'all') {
      constraints.unshift(where('environment', '==', environment));
    }
    if (hideInternal) {
      constraints.unshift(where('isInternal', '==', false));
    }

    const q = query(collection(db, 'engagement'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setActiveSessions(docs);
        setConnected(true);

        if (isInitialRef.current) {
          // First snapshot — just capture baseline event counts, show nothing
          docs.forEach(d => {
            prevCountsRef.current.set(d.id, d.events?.length || 0);
          });
          isInitialRef.current = false;
          return;
        }

        // Subsequent snapshots — diff each session for new events
        const newItems = [];
        docs.forEach(doc => {
          const prevCount = prevCountsRef.current.get(doc.id) ?? 0;
          const currentEvents = doc.events || [];
          const currentCount = currentEvents.length;

          if (currentCount > prevCount) {
            // Slice the newly appended events
            currentEvents.slice(prevCount).forEach(evt => {
              newItems.push({
                feedId: `${doc.id}_${evt.ts}_${Math.random().toString(36).slice(2, 6)}`,
                sessionId: doc.id,
                shortId: doc.id.split('_').pop() || doc.id.slice(-8),
                type: evt.type,
                data: evt.data || {},
                ts: evt.ts,
                arrivedAt: Date.now(),
              });
            });
            prevCountsRef.current.set(doc.id, currentCount);
          } else if (!prevCountsRef.current.has(doc.id)) {
            // Brand new session appeared in the top-15 window
            prevCountsRef.current.set(doc.id, currentCount);
          }
        });

        if (newItems.length > 0) {
          // Prepend newest items (reverse so oldest-new is at top of prepend)
          setFeed(prev =>
            [...newItems.reverse(), ...prev].slice(0, FEED_MAX_LENGTH)
          );
        }
      },
      (err) => {
        console.error('[useLiveSessions] Snapshot error:', err);
        setConnected(false);
      }
    );

    return () => {
      unsubscribe();
      setConnected(false);
      isInitialRef.current = true;
      prevCountsRef.current = new Map();
    };
  }, [enabled, environment, hideInternal]);

  return { feed, activeSessions, connected };
};
